import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Card,
  Table,
  Input,
  InputNumber,
  Button,
  message,
  Space,
} from "antd";
import { DownloadOutlined, ReloadOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { Booking, Product } from "../../types/booking";
import { updateBookingProduct } from "../../services/agentPortalService";
import { generateAndSaveShippingLabel } from "../../utils/shippingLabel";
import { supabase } from "../../lib/supabase";
import { getBookingProducts } from "../../utils/agentBookingMetrics";
import { AGENT_TABLE_SCROLL_X } from "../../config/agentPortalLayout";
import { useDebouncedSave } from "../../hooks/useDebouncedSave";
import { AgentAutoSaveStatus } from "./AgentAutoSaveStatus";
import { notifyAgentSaveSuccess } from "../../utils/agentAutoSaveFeedback";

type ProductField = keyof Pick<
  Product,
  "name" | "ean" | "cartons" | "pieces" | "gross_weight" | "cbm" | "value"
>;

interface AgentProductsTableProps {
  booking: Booking;
  onUpdated: () => void;
}

export const AgentProductsTable: React.FC<AgentProductsTableProps> = ({
  booking,
  onUpdated,
}) => {
  const initialProducts = getBookingProducts(booking);
  const [products, setProducts] = useState<Product[]>(initialProducts);
  const [generatingId, setGeneratingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const savingRef = useRef<Set<number>>(new Set());
  const scheduleSave = useDebouncedSave(600);

  useEffect(() => {
    setProducts(getBookingProducts(booking));
  }, [booking]);

  const persistProduct = useCallback(
    async (productId: number, updates: Partial<Product>) => {
      if (savingRef.current.has(productId)) return;
      savingRef.current.add(productId);
      setSaving(true);
      try {
        const payload = { ...updates };
        if (payload.value === null) {
          delete payload.value;
        }
        const { error } = await updateBookingProduct(productId, payload);
        if (error) throw error;
        setLastSaved(new Date());
        notifyAgentSaveSuccess("Product", "agent-product-save");
      } catch (err) {
        console.error(err);
        message.error("Failed to save product");
      } finally {
        savingRef.current.delete(productId);
        if (savingRef.current.size === 0) {
          setSaving(false);
        }
      }
    },
    [onUpdated]
  );

  const updateField = (
    productId: number,
    field: ProductField,
    value: string | number | null
  ) => {
    setProducts((prev) =>
      prev.map((p) => (p.id === productId ? { ...p, [field]: value } : p))
    );
    scheduleSave(`product-${productId}-${field}`, () =>
      persistProduct(productId, { [field]: value })
    );
  };

  const handleRegenerateLabel = async (product: Product) => {
    setGeneratingId(product.id);
    try {
      const company = Array.isArray(booking.company_info)
        ? booking.company_info[0]
        : booking.company_info;
      const supplier = Array.isArray(booking.supplier_info)
        ? booking.supplier_info[0]
        : booking.supplier_info;
      const transport = Array.isArray(booking.transport_type_info)
        ? booking.transport_type_info[0]
        : booking.transport_type_info;
      const incoterm = Array.isArray(booking.booking_incoterm_info)
        ? booking.booking_incoterm_info[0]
        : booking.booking_incoterm_info;
      const originPort = Array.isArray(booking.origin_port_info)
        ? booking.origin_port_info[0]
        : booking.origin_port_info;
      const destPort = Array.isArray(booking.destination_port_info)
        ? booking.destination_port_info[0]
        : booking.destination_port_info;

      const labelPath = await generateAndSaveShippingLabel(
        {
          id: booking.id,
          reference: booking.reference || "",
          transport_type: {
            name: transport?.name || "",
            abbreviation: transport?.abbreviation || "",
          },
          booking_type: booking.booking_type_info
            ? {
                name: Array.isArray(booking.booking_type_info)
                  ? booking.booking_type_info[0]?.name || ""
                  : booking.booking_type_info.name || "",
              }
            : undefined,
          booking_incoterm: { name: incoterm?.name || "N/A" },
          origin_port: originPort
            ? { name: originPort.name, country: originPort.country || "" }
            : undefined,
          destination_port: destPort
            ? { name: destPort.name, country: destPort.country || "" }
            : undefined,
          supplier: {
            name: supplier?.name || "",
            email: supplier?.email || "",
            phone: supplier?.phone || "",
            country: supplier?.country || "",
            city: supplier?.city || "",
            address: supplier?.address || "",
          },
        },
        { ...product, company_id: booking.company_id || 0 },
        company?.name || "Company"
      );

      if (!labelPath) {
        message.error("Failed to generate label");
        return;
      }

      await supabase
        .from("booking_product")
        .update({ shipping_label: labelPath })
        .eq("id", product.id);

      message.success("Label regenerated");
      onUpdated();
    } catch (err) {
      console.error(err);
      message.error("Failed to regenerate label");
    } finally {
      setGeneratingId(null);
    }
  };

  const handleDownloadLabel = async (product: Product) => {
    if (!product.shipping_label) {
      message.warning("No label available — regenerate first");
      return;
    }
    try {
      const { data, error } = await supabase.storage
        .from("shipping_labels")
        .download(product.shipping_label);
      if (error || !data) throw error;
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `shipping-label-${booking.id}-${product.id}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      message.error("Failed to download label");
    }
  };

  const columns: ColumnsType<Product> = [
    {
      title: "Name",
      dataIndex: "name",
      width: 160,
      render: (value: string, record) => (
        <Input
          size="small"
          value={value}
          onChange={(e) => updateField(record.id, "name", e.target.value)}
        />
      ),
    },
    {
      title: "EAN",
      dataIndex: "ean",
      width: 120,
      render: (value: string | null, record) => (
        <Input
          size="small"
          value={value || ""}
          onChange={(e) => updateField(record.id, "ean", e.target.value || null)}
        />
      ),
    },
    {
      title: "Cartons",
      dataIndex: "cartons",
      width: 80,
      render: (value: number | null, record) => (
        <InputNumber
          size="small"
          min={0}
          value={value ?? undefined}
          onChange={(v) => updateField(record.id, "cartons", v ?? 0)}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Pieces",
      dataIndex: "pieces",
      width: 80,
      render: (value: number | null, record) => (
        <InputNumber
          size="small"
          min={0}
          value={value ?? undefined}
          onChange={(v) => updateField(record.id, "pieces", v ?? 0)}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Gross kg",
      dataIndex: "gross_weight",
      width: 90,
      render: (value: number, record) => (
        <InputNumber
          size="small"
          min={0}
          step={0.1}
          value={value}
          onChange={(v) => updateField(record.id, "gross_weight", v ?? 0)}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "CBM",
      dataIndex: "cbm",
      width: 90,
      render: (value: number, record) => (
        <InputNumber
          size="small"
          min={0}
          step={0.001}
          value={value}
          onChange={(v) => updateField(record.id, "cbm", v ?? 0)}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Value USD",
      dataIndex: "value",
      width: 90,
      render: (value: number | null, record) => (
        <InputNumber
          size="small"
          min={0}
          precision={2}
          value={value ?? undefined}
          onChange={(v) =>
            updateField(record.id, "value", v != null ? v : null)
          }
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Label",
      key: "label",
      width: 180,
      render: (_, record) => (
        <Space size={4} wrap>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => handleDownloadLabel(record)}
            disabled={!record.shipping_label}
          />
          <Button
            size="small"
            icon={<ReloadOutlined />}
            loading={generatingId === record.id}
            onClick={() => handleRegenerateLabel(record)}
          />
        </Space>
      ),
    },
  ];

  if (products.length === 0) {
    return (
      <Card size="small" title="Products">
        <p style={{ margin: 0 }}>No products on this booking</p>
      </Card>
    );
  }

  return (
    <Card size="small" title={`Products (${products.length})`}>
      <Table
        rowKey="id"
        size="small"
        pagination={false}
        columns={columns}
        dataSource={products}
        scroll={{ x: AGENT_TABLE_SCROLL_X }}
      />
      <AgentAutoSaveStatus saving={saving} lastSaved={lastSaved} />
    </Card>
  );
};
